import { useMemo, useEffect } from 'react';
import { Draggable, Droppable } from '@hello-pangea/dnd';

import { EventPricingTier } from 'legacy/common/types/models';
import {
  EventTierCreation,
  TierDto,
  TierReorderDto
} from 'legacy/events/types';

import Ticket from './Ticket';
import classNames from 'classnames';

type TicketsProps = {
  eventId: string;
  eventShareToken: string;
  tickets: Array<TierDto>;
  selectedTicket: TierDto;
  selectedIndex: number;
  toggleTicketTier: (id: string, isDisabled: boolean) => void;
  toggleHideTier: (id: string) => void;
  setSelectedIndex: (id: string) => void;
  setSelectedTicket: (ticket: TierDto) => void;
  isSortable?: boolean;
  sectionId?: string;
  onReorder?: (tickets: Array<TierReorderDto>) => void;
  lastCreatedTicketId?: string;
};

const Tickets: React.FC<TicketsProps> = ({
  eventId,
  eventShareToken,
  tickets,
  isSortable,
  toggleTicketTier,
  toggleHideTier,
  setSelectedIndex,
  setSelectedTicket,
  sectionId,
  lastCreatedTicketId
}) => {
  // Sort tickets by sortIndex
  const sortedTickets = useMemo(
    () => tickets.sort((a, b) => a.sortIndex - b.sortIndex),
    [tickets]
  );

  // Scroll to newly added ticket
  useEffect(() => {
    if (lastCreatedTicketId) {
      setTimeout(() => {
        const scrollableParent = document.querySelector(
          '#modal-scroll-container'
        );
        const ticketElement = document.querySelector(
          `[data-ticket-id="${lastCreatedTicketId}"]`
        );

        if (scrollableParent && ticketElement) {
          const ticketRect = ticketElement.getBoundingClientRect();
          const containerRect = scrollableParent.getBoundingClientRect();
          const scrollTop =
            scrollableParent.scrollTop +
            ticketRect.top -
            containerRect.top -
            20; // 20px padding from top

          scrollableParent.scrollTo({
            top: scrollTop
          });
        }
      }, 100);
    }
  }, [lastCreatedTicketId]);

  if (!isSortable) {
    return (
      <div className="w-full py-6 pb-10">
        <div className="flex flex-col">
          {sortedTickets.map((ticket: any) => (
            <div key={ticket.id} data-ticket-id={ticket.id}>
              <Ticket
                eventId={eventId}
                eventShareToken={eventShareToken}
                ticket={ticket}
                toggleTicketTier={(isDisabled: boolean) => {
                  toggleTicketTier(ticket.id, isDisabled);
                }}
                onClick={() => {
                  setSelectedTicket(ticket);
                  setSelectedIndex(ticket.id);
                }}
                isSortable={isSortable}
                toggleHideTier={() =>
                  toggleHideTier(
                    (ticket as EventPricingTier)?._id ||
                      (ticket as EventTierCreation).id
                  )
                }
                lastCreatedTicketId={lastCreatedTicketId}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Droppable droppableId={sectionId} type="tiers">
      {(providedTiers, snapshot) => (
        <div
          className={classNames(
            'w-full px-2 py-6 transition-colors duration-200',
            {
              'bg-secondary-50 rounded-lg': snapshot.isDraggingOver
            }
          )}
          ref={providedTiers.innerRef}
          data-rfd-droppable-context-id={
            providedTiers.droppableProps['data-rfd-droppable-context-id']
          }
          data-rfd-droppable-id={
            providedTiers.droppableProps['data-rfd-droppable-id']
          }
        >
          <div className="flex flex-col gap-4">
            {sortedTickets.map((ticket: any, index: number) => (
              <Draggable index={index} key={ticket.id} draggableId={ticket.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    data-ticket-id={ticket.id}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...provided.draggableProps}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...provided.dragHandleProps}
                    className={classNames('relative', {
                      'bg-primary-100 rounded-lg shadow-lg border-1 border-primary-500':
                        snapshot.isDragging,
                      'bg-white rounded-md': !snapshot.isDragging
                    })}
                  >
                    <Ticket
                      eventId={eventId}
                      eventShareToken={eventShareToken}
                      ticket={ticket}
                      toggleTicketTier={(isDisabled: boolean) => {
                        toggleTicketTier(ticket.id, isDisabled);
                      }}
                      toggleHideTier={() =>
                        toggleHideTier(
                          (ticket as EventPricingTier)?._id ||
                            (ticket as EventTierCreation).id
                        )
                      }
                      onClick={() => {
                        setSelectedTicket(ticket);
                        setSelectedIndex(ticket.id);
                      }}
                      isSortable={isSortable}
                      lastCreatedTicketId={lastCreatedTicketId}
                    />
                  </div>
                )}
              </Draggable>
            ))}
          </div>

          {sortedTickets?.length === 0 ? (
            <div className="flex items-center justify-center py-24 border border-dashed rounded-lg">
              <p className="text-gray-500">Drag and Drop a ticket here</p>
            </div>
          ) : (
            providedTiers.placeholder
          )}
        </div>
      )}
    </Droppable>
  );
};

export default Tickets;
